/**
 * The contract between the decoder and everything downstream.
 *
 * `DecodedImage` is deliberately renderer-shaped: scene-linear half-float RGB
 * at a working resolution, plus the facts the UI and planner need. The original
 * bytes are never modified and never round-tripped through this structure.
 */

export type SourceKind = 'raw' | 'tiff' | 'bitmap';

export interface CaptureFacts {
  cameraMake: string;
  cameraModel: string;
  lens: string;
  iso: number;
  shutter: number;
  aperture: number;
  focalLength: number;
  /** ISO-8601, or empty when the file carries no capture time. */
  capturedAt: string;
  /** EXIF orientation as quarter-turns already applied by the decoder. */
  orientationApplied: number;
}

export interface DecodedImage {
  /** Working-resolution pixel data: RGB triplets, scene-linear, 0..~1 nominal. */
  data: Float32Array;
  width: number;
  height: number;

  /** Full-resolution dimensions of the source, before any working downscale. */
  fullWidth: number;
  fullHeight: number;

  kind: SourceKind;
  /** True when the buffer came from an embedded preview rather than the sensor data. */
  fromEmbeddedPreview: boolean;

  capture: CaptureFacts;

  /** As-shot white balance, estimated from the camera matrices where possible. */
  asShot: { kelvin: number; tint: number; estimated: boolean };

  /** Non-fatal decoder notes surfaced in the UI rather than swallowed. */
  warnings: string[];
}

export interface DecodeRequest {
  id: string;
  buffer: ArrayBuffer;
  filename: string;
  /** Longest edge of the interactive working image. */
  maxWorkingEdge: number;
  /** Full-resolution decode for export. Slower and much larger. */
  full?: boolean;
}

export interface DecodeProgress {
  id: string;
  stage: 'reading' | 'decoding' | 'converting' | 'done';
  message: string;
}

export type DecodeResponse =
  | { type: 'progress'; id: string; stage: DecodeProgress['stage']; message: string }
  | { type: 'done'; id: string; image: DecodedImage }
  | { type: 'error'; id: string; message: string };

export const RAW_EXTENSIONS = [
  'cr3',
  'cr2',
  'crw',
  'nef',
  'nrw',
  'arw',
  'srf',
  'sr2',
  'dng',
  'raf',
  'orf',
  'rw2',
  'raw',
  'pef',
  'srw',
  'x3f',
  'erf',
  'kdc',
  'mrw',
  'iiq',
  '3fr',
  'fff',
  'mos',
  'gpr',
] as const;

export const TIFF_EXTENSIONS = ['tif', 'tiff'] as const;
export const BITMAP_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'avif', 'heic', 'heif'] as const;

export function extensionOf(filename: string): string {
  const index = filename.lastIndexOf('.');
  return index === -1 ? '' : filename.slice(index + 1).toLowerCase();
}

export function classifySource(filename: string): SourceKind | null {
  const extension = extensionOf(filename);
  if ((RAW_EXTENSIONS as readonly string[]).includes(extension)) return 'raw';
  if ((TIFF_EXTENSIONS as readonly string[]).includes(extension)) return 'tiff';
  if ((BITMAP_EXTENSIONS as readonly string[]).includes(extension)) return 'bitmap';
  return null;
}

export const ACCEPTED_EXTENSIONS = [...RAW_EXTENSIONS, ...TIFF_EXTENSIONS, ...BITMAP_EXTENSIONS];

export function emptyCaptureFacts(): CaptureFacts {
  return {
    cameraMake: '',
    cameraModel: '',
    lens: '',
    iso: 0,
    shutter: 0,
    aperture: 0,
    focalLength: 0,
    capturedAt: '',
    orientationApplied: 0,
  };
}

/** "1/125 s · f/5.0 · ISO 400 · 105 mm" — the strip under the histogram. */
export function formatCapture(capture: CaptureFacts): string {
  const parts: string[] = [];
  if (capture.shutter > 0) {
    parts.push(capture.shutter >= 1 ? `${capture.shutter.toFixed(1)}s` : `1/${Math.round(1 / capture.shutter)}`);
  }
  if (capture.aperture > 0) parts.push(`ƒ/${capture.aperture.toFixed(1)}`);
  if (capture.iso > 0) parts.push(`ISO ${Math.round(capture.iso)}`);
  if (capture.focalLength > 0) parts.push(`${Math.round(capture.focalLength)}mm`);
  return parts.join('  ·  ');
}
